import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { styled } from '@mui/material/styles';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CircleIcon from '@mui/icons-material/Circle';
import PageHelpPanel from './PageHelpPanel';
import FeedbackBoardLauncher from './FeedbackBoardLauncher';
import { logoutAndRedirect } from '../utils/authSession';
import { useUiVersion } from '../context/UiVersionContext';
import { counterpartRoute } from '../config/menuManifest';

import './Header.css'; // CSS за стилизация

const AppBar = styled(MuiAppBar)(({ theme }) => ({
  width: '100%',
  left: 0,
  right: 0,
  marginLeft: 0,
  zIndex: theme.zIndex.drawer + 1,
}));



function Header({ open, setOpen, isMobile = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { uiVersion, setUiVersion } = useUiVersion();

  // Toggle handler: persist the choice AND move the user to the counterpart of the current page in
  // the chosen version (e.g. /pmi-devicesP → /pmi-devices-v2). Pages with no counterpart stay put.
  const handleUiVersionSelect = (nextVersion) => {
    if (nextVersion === uiVersion) return;
    setUiVersion(nextVersion);
    const target = counterpartRoute(location.pathname, nextVersion);
    if (target && target !== location.pathname) navigate(target);
  };

  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [releaseMeta, setReleaseMeta] = useState(null);
  const profileMenuOpen = Boolean(profileAnchorEl);
  const token = localStorage.getItem('token');
  const decodedToken = useMemo(() => {
    if (!token) return {};
    try {
      return jwtDecode(token) || {};
    } catch (error) {
      return {};
    }
  }, [token]);

  const pageTitle = useMemo(() => {
    const routeLabels = [
      { prefix: '/pmi-devicesP', label: 'PMI Devices Platform' },
      { prefix: '/pmi-devices', label: 'PMI Devices' },
      { prefix: '/campaignsP', label: 'Campaigns Platform' },
      { prefix: '/campaigns', label: 'Campaigns' },
      { prefix: '/campaign-previews', label: 'Campaign Previews' },
      { prefix: '/schedule', label: 'Scheduled Playback' },
      { prefix: '/proof-of-play', label: 'Proof of Play' },
      { prefix: '/dashboard-sofia-edition-live', label: 'Sofia Wallboard' },
      { prefix: '/dashboard-sofia-edition', label: 'Sofia Dashboard' },
      { prefix: '/weekly-report', label: 'Weekly Report' },
      { prefix: '/inactive-escalation', label: 'Inactive SLA' },
      { prefix: '/campaign-change-logs', label: 'Campaign Change Logs' },
      { prefix: '/campaign-edit-logs', label: 'Campaign Change Logs' },
      { prefix: '/file-edit-logs', label: 'File Edit Logs' },
      { prefix: '/roles-permissions', label: 'Roles & Permissions' },
      { prefix: '/access-control', label: 'Access Control' },
      { prefix: '/access-audit-logs', label: 'Audit Log' },
      { prefix: '/dashboard', label: 'Dashboard' },
    ];
    const found = routeLabels.find((item) => location.pathname.startsWith(item.prefix));
    return found?.label || 'Workspace';
  }, [location.pathname]);

  const userRoles = Array.isArray(decodedToken?.roles) ? decodedToken.roles : [];
  const primaryRole = String(userRoles[0] || 'viewer').trim() || 'viewer';
  const userEmail = String(decodedToken?.email || '').trim();
  const userName =
    String(decodedToken?.nameLocal || '').trim() ||
    String(decodedToken?.name || '').trim() ||
    String(decodedToken?.username || '').trim() ||
    (userEmail ? userEmail.split('@')[0] : 'User');
  const userInitials = useMemo(() => {
    const parts = userName.split(/\s+/).filter(Boolean).slice(0, 2);
    if (!parts.length) return 'U';
    return parts.map((part) => part[0].toUpperCase()).join('');
  }, [userName]);

  useEffect(() => {
    let cancelled = false;

    fetch('/release.json', { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (cancelled) return;
        setReleaseMeta(payload && typeof payload === 'object' ? payload : null);
      })
      .catch(() => {
        if (cancelled) return;
        setReleaseMeta(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const environmentMeta = useMemo(() => {
    const host = String(window.location.hostname || '').toLowerCase();
    const target = String(releaseMeta?.target || '').trim().toLowerCase();
    let normalizedTarget = target;

    if (host === 'staging.on-labs.eu' || host.startsWith('staging.')) {
      normalizedTarget = 'staging';
    } else if (host === 'on-labs.eu' || host === 'www.on-labs.eu') {
      normalizedTarget = 'production';
    } else if (!normalizedTarget) {
      normalizedTarget = 'production';
    }

    const label = normalizedTarget === 'staging' ? 'STAGING' : 'PRODUCTION';
    const versionLabel = String(releaseMeta?.commit || releaseMeta?.release || '')
      .trim()
      .slice(0, 8);

    return {
      target: normalizedTarget,
      label,
      versionLabel,
      release: String(releaseMeta?.release || '').trim(),
      commit: String(releaseMeta?.commit || '').trim(),
      builtAt: String(releaseMeta?.builtAt || '').trim(),
    };
  }, [releaseMeta]);

  const environmentTooltip = useMemo(() => {
    const lines = [environmentMeta.label];
    if (environmentMeta.release) lines.push(`Release: ${environmentMeta.release}`);
    if (environmentMeta.commit) lines.push(`Commit: ${environmentMeta.commit}`);
    if (environmentMeta.builtAt) lines.push(`Built: ${environmentMeta.builtAt}`);
    return lines.join('\n');
  }, [environmentMeta]);
  
  const handleDrawerOpen = () => {
    // Legacy sidebar (non-v2 routes). Harmless on v2 routes where it isn't rendered.
    setOpen(true);
    // v2 shell routes render their own sidebar — let it toggle its mobile drawer off the same
    // header menu button instead of a second floating hamburger.
    window.dispatchEvent(new Event('pmi-v2-sidebar-toggle'));
  };
  const handleProfileMenuOpen = (event) => {
    setProfileAnchorEl(event.currentTarget);
  };
  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };
  const handleLogout = () => {
    logoutAndRedirect();
  };

  return (
    <AppBar position="fixed" className="platform-header-appbar">
        <Toolbar className="platform-header">
          <Box className="platform-header__left">
	          <IconButton
            color="default"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            className="platform-header__menu-trigger"
	            sx={[
	              {
	                mr: 1.25,
	              },
	              open && !isMobile && { display: 'none' },
	            ]}
	          >
            <MenuIcon />
          </IconButton>
          <img src="/onlabs-logo-fff.png" alt="Logo" className="header-logo" />
          <Box className="platform-header__title-wrap">
            <Typography className="platform-header__platform-title">On labs</Typography>
            <Typography className="platform-header__page-title">{pageTitle}</Typography>
          </Box>
          <Box className="platform-header__partner">
            <span className="platform-header__partner-label">Designed for</span>
            <img
              src="/pmi-logo.svg"
              alt="Philip Morris International"
              className="platform-header__partner-logo"
            />
          </Box>
          </Box>
          <Box className="platform-header__right">
            <div
              className="platform-header__ui-version"
              role="group"
              aria-label="UI version"
              title="Превключи между стария (v1) и новия (v2) дизайн на страниците"
              style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid rgba(127,127,127,0.45)', background: 'rgba(127,127,127,0.12)', borderRadius: 999, overflow: 'hidden', height: 26 }}
            >
              {['v1', 'v2'].map((v) => {
                const active = uiVersion === v;
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => handleUiVersionSelect(v)}
                    aria-pressed={active}
                    style={{
                      appearance: 'none',
                      border: 0,
                      cursor: 'pointer',
                      padding: '0 12px',
                      height: '100%',
                      fontSize: 12,
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      // Active: solid white pill + shadow (reads on any header bg). Inactive:
                      // transparent with `inherit` so the text adopts the header's own color
                      // (white on the blue legacy header, dark on the light v2 header).
                      color: active ? '#12305c' : 'inherit',
                      background: active ? '#ffffff' : 'transparent',
                      boxShadow: active ? '0 1px 2px rgba(0,0,0,0.25)' : 'none',
                      opacity: active ? 1 : 0.85,
                    }}
                  >
                    {v}
                  </button>
                );
              })}
            </div>
            <PageHelpPanel />
            <FeedbackBoardLauncher />
            <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{environmentTooltip}</span>}>
              <Box
                className={`platform-header__env-chip platform-header__env-chip--${environmentMeta.target}`}
                role="status"
                aria-label={`${environmentMeta.label} ${environmentMeta.versionLabel || ''}`.trim()}
              >
                <span className="platform-header__env-label">{environmentMeta.label}</span>
                {environmentMeta.versionLabel ? (
                  <span className="platform-header__env-version">{environmentMeta.versionLabel}</span>
                ) : null}
              </Box>
            </Tooltip>
            <Chip
              icon={<CircleIcon className="platform-header__live-dot" />}
              label="Live"
              size="small"
              className="platform-header__live-chip"
            />
            <Tooltip title={userEmail || 'No email in token'}>
              <button
                type="button"
                className="platform-profile-trigger"
                onClick={handleProfileMenuOpen}
                aria-expanded={profileMenuOpen ? 'true' : 'false'}
              >
                <Avatar className="platform-profile-trigger__avatar">{userInitials}</Avatar>
                <span className="platform-profile-trigger__info">
                  <strong>{userName}</strong>
                  <small>{primaryRole}</small>
                </span>
                <KeyboardArrowDownIcon fontSize="small" />
              </button>
            </Tooltip>
          </Box>
        </Toolbar>
        <Menu
          anchorEl={profileAnchorEl}
          open={profileMenuOpen}
          onClose={handleProfileMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: { className: 'platform-profile-menu' },
          }}
        >
          <Box className="platform-profile-menu__header">
            <Avatar className="platform-profile-menu__avatar">{userInitials}</Avatar>
            <Box className="platform-profile-menu__identity">
              <Typography variant="subtitle2">{userName}</Typography>
              <Typography variant="caption">{userEmail || 'No email available'}</Typography>
            </Box>
          </Box>
          <Box className="platform-profile-menu__roles">
            {(userRoles.length ? userRoles : ['viewer']).map((role) => (
              <Chip key={role} size="small" label={role} />
            ))}
          </Box>
          <Divider />
          <MenuItem disabled>
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" />
            </ListItemIcon>
            Signed in session
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            Logout
          </MenuItem>
        </Menu>
      </AppBar>
  );
}

export default Header;
